import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const wiz = defineConnector({
  identifier: "wiz",
  name: "Wiz",
  tagLine: "Cloud security platform",
  keywords:
    "cnapp cspm cwpp cloud security posture management vulnerability graphql",
  logo,
  protocol: "http",
  tags: ["featured", "security"],
  defaultUrlPattern: () =>
    "https://api\\.[a-z0-9]+\\.(app|gov)\\.wiz\\.(io|us)/.*",
  auth: oauthAuth,
  steps: [
    {
      title: "Create a service account in Wiz",
      items: [
        {
          kind: "markdown",
          content: `1. Go to [Settings › Access Management › Service Accounts](https://app.wiz.io/settings/service-accounts), then click "Add Service Account"
2. Enter a name (e.g., "3B")
3. Choose type: Custom Integration (GraphQL API), then select projects, expiration, and API scopes
4. Click "Add Service Account"
5. Copy the Client ID, Client Secret, and Token URL`,
        },
      ],
    },
    {
      title: "Enter your token URL",
      items: [
        {
          kind: "markdown",
          content:
            "On the service accounts page, enter the token URL displayed at the top of the page.",
        },
        {
          kind: "baseUrl",
          label: "Token URL",
          required: true,
          defaultValue: "https://auth.app.wiz.io/oauth/token",
          helperText: "This URL should end in /token.",
          target: { kind: "configuration", field: "tokenUrl" },
        },
      ],
    },
    {
      title: "Enter your GraphQL endpoint",
      items: [
        {
          kind: "markdown",
          content:
            "Navigate to [Tenant Info](https://app.wiz.io/settings/tenant-info) and copy the API Endpoint URL.",
        },
        {
          kind: "baseUrl",
          label: "GraphQL endpoint URL",
          required: true,
          placeholder: "https://api.us1.app.wiz.io/graphql",
          target: { kind: "configuration", field: "graphqlUrl" },
        },
      ],
    },
    {
      title: "Enter your credentials",
      items: [
        {
          kind: "input",
          label: "Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "client_credentials",
    tokenEndpoint: ({ tokenUrl }) => tokenUrl ?? "",
    clientCredentialsFrom: "db",
    tokenEndpointAuthMethod: () => "client_secret_post",
    scopes: () => [],
    authorizationParameters: () => ({
      audience: "wiz-api",
    }),
  },
  connectionTest: {
    protocol: "http",
    request: ({ graphqlUrl }) => ({
      url: graphqlUrl ?? "",
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query: "{ viewer { id } }" }),
    }),
  },
  publicEnvVars: ({ graphqlUrl }) => ({
    WIZ_GRAPHQL_URL: graphqlUrl ?? "",
  }),
  usageHint: () =>
    "The Wiz API is regional — send all GraphQL queries to the endpoint in the WIZ_GRAPHQL_URL environment variable; other regions reject this tenant’s token. Authentication is injected automatically, so never fetch tokens yourself.",
});
